import { useMemo, useState } from 'react';
import { DevicePreview, DEVICE_PANELS, deviceCapabilities, type DeviceVariant } from '@kioskforge/blocks';
import { layoutRunsOn } from '@kioskforge/layout';
import type { Layout } from '@kioskforge/shared';
import { deviceProfile } from '../../features/layouts/deviceProfile';
import { DeviceFrame, Select, Tabs } from '../ui';

/**
 * A5b.11 / A5b.12 — the rendered device preview.
 *
 * Two switches drive a `DeviceFrame` bezel (style.md §7) whose mono caption names
 * the class and true pixel size:
 *  - device variant (tablet landscape / portrait / ESP32) — the canvas is the
 *    preview-only renderer from `packages/blocks` (A5b.10);
 *  - screen (when the layout has more than one) so a non-home screen's blocks,
 *    e.g. a check-in form, can be reviewed.
 * Blocks the selected class cannot run are greyed with a reason (A5b.12). The
 * whole-layout run/blocked status is reported for BOTH classes. No network calls.
 */
const VARIANTS: Array<{ id: DeviceVariant; label: string }> = [
  { id: 'tablet-landscape', label: 'Tablet landscape' },
  { id: 'tablet-portrait', label: 'Tablet portrait' },
  { id: 'esp32', label: 'ESP32' }
];

const CAPTION: Record<DeviceVariant, string> = {
  'tablet-landscape': 'TABLET 1280×800',
  'tablet-portrait': 'TABLET 800×1280',
  esp32: 'ESP32 800×480'
};

export function DevicePreviewPanel({
  layout,
  minLockdown,
  initialVariant = 'tablet-landscape'
}: {
  layout: Layout;
  minLockdown?: 'none' | 'pwa' | 'shell';
  initialVariant?: DeviceVariant;
}) {
  const [variantId, setVariantId] = useState<DeviceVariant>(initialVariant);
  const [screenId, setScreenId] = useState(layout.home);
  const variant = VARIANTS.find((item) => item.id === variantId) ?? { id: initialVariant, label: initialVariant };
  const panel = DEVICE_PANELS[variant.id];
  const screen = layout.screens.find((item) => item.id === screenId) ?? layout.screens[0];
  const capabilities = useMemo(() => deviceCapabilities(panel.deviceClass), [panel.deviceClass]);
  const result = useMemo(() => layoutRunsOn(layout, capabilities, minLockdown), [layout, capabilities, minLockdown]);
  // A5b.12 evidence: the two classes can differ on the same layout.
  const otherClass = panel.deviceClass === 'esp32' ? 'tablet' : 'esp32';
  const otherOk = layoutRunsOn(layout, deviceProfile(otherClass)).ok;

  return (
    <div className="preview-panel">
      <div className="preview-controls">
        <span className="index-label">DEVICE</span>
        <Tabs
          items={VARIANTS.map((item) => item.label)}
          value={variant.label}
          onChange={(label) => {
            const next = VARIANTS.find((item) => item.label === label);
            if (next) setVariantId(next.id);
          }}
        />
      </div>
      {layout.screens.length > 1 && screen ? (
        <div className="preview-controls">
          <label className="index-label" htmlFor="preview-screen">SCREEN</label>
          <Select id="preview-screen" value={screen.id} onChange={(event) => setScreenId(event.target.value)}>
            {layout.screens.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </Select>
        </div>
      ) : null}
      <DeviceFrame label={CAPTION[variant.id]} panelWidth={panel.width} panelHeight={panel.height} fit={432}>
        <DevicePreview layout={layout} variant={variant.id} screenId={screen?.id} capabilities={capabilities} />
      </DeviceFrame>
      <p className={result.ok ? 'preview-status capability-ok' : 'preview-status capability-blocked'}>
        {result.ok
          ? `${panel.deviceClass}: layout runs`
          : `${panel.deviceClass}: layout blocked — ${result.reasons.join(' ')}`}
      </p>
      <p className="preview-muted" data-other-class={otherClass} data-other-ok={otherOk ? 'true' : 'false'}>
        {otherClass}: {otherOk ? 'runs' : 'blocked'}
      </p>
    </div>
  );
}
